/**
 * Icon lookup and caching: the IconService class and its helpers.
 *
 * Deliberately free of `../app`. The AngularJS registration lives in the
 * sibling `icons-service.ts`, which re-exports everything here, so existing
 * importers of that path are unchanged. Splitting them lets Node-side callers
 * — the headless plan reference and the harnesses behind it — reach
 * IconServiceHelper without pulling in the Angular module and jQuery
 * (issue #1071).
 */

// Type-only: authorized-http-service imports the AngularJS module at its top
// level, so a value import here would drag Angular back into a module whose
// whole purpose is to load without it.
import type { ISimpleHttpService } from './authorized-http-service';
import type { IconsReturnModel } from './icons-service-models';

export const ICON_UPLOAD_ERROR_TOO_BIG = "icon_err_too_big";
export const ICON_UPLOAD_MAX_SIZE_BYTES = 1025 * 1025;
export const ICON_MEETING_TAG = "meeting";
export const ICON_RESOURCES_TAG = "resource";
export const ICON_EQUIPMENT_TAG = "equipment";
export const ICON_MILESTONE_TAG = "milestone";
export const ICON_LOGO_TAG = "logo";

export interface IHasTags {
    tags: string[];
}
export interface IHasUserState {
    isUserIcon: boolean;
}

export interface IIconInfo {
    id: string;
    name: string;
    category: string;
    tags: string[];
    url: string;
    baseX: number;
    baseY: number;
    isUserIcon: boolean;
    isDeleted?: boolean;
    /** AI-chat short id (system icons, ?source=system listing only). */
    shortcode?: string;
}

export interface INewIconValues {
    name?: string;
    category?: string;
    tags?: string[];
    url?: string;
    baseX?: number;
    baseY?: number;
}

interface IIconFromServer {
    id: string;
    name: string;
    category: string;
    tags: string[];
    url: string;
    baseX: number;
    baseY: number;
    isSystemIcon: boolean;
    isDeleted?: boolean;
    shortcode?: string;
}

interface IEditIconBindingModel {
    name?: string;
    category?: string;
    tags?: string[];
    url?: string;
    baseX?: number;
    baseY?: number;
}

export class IconServiceHelper {
    static hasTag(icon: IHasTags, tag: string) {
        return icon.tags.filter(t => t.toUpperCase() === tag.toUpperCase()).length > 0
    }

    static doesNotHaveTag(icon: IHasTags, tag: string) {
        return icon.tags.filter(t => t.toUpperCase() === tag.toUpperCase()).length === 0
    }

    static isTaggedForTasks(icon: IHasTags) {
        return IconServiceHelper.doesNotHaveTag(icon, ICON_RESOURCES_TAG) && IconServiceHelper.doesNotHaveTag(icon, ICON_MILESTONE_TAG);
    }

    static isTaggedForMeetings(icon: IHasTags) {
        return IconServiceHelper.hasTag(icon, ICON_MEETING_TAG);
    }

    static isTaggedForResources(icon: IHasTags) {
        return IconServiceHelper.hasTag(icon, ICON_RESOURCES_TAG);
    }

    static isTaggedForEquipment(icon: IHasTags) {
        return IconServiceHelper.hasTag(icon, ICON_EQUIPMENT_TAG);
    }

    static isTaggedForMilestones(icon: IHasTags) {
        return IconServiceHelper.hasTag(icon, ICON_MILESTONE_TAG);
    }

    static isTaggedForLogos(icon: IHasTags) {
        return IconServiceHelper.hasTag(icon, ICON_LOGO_TAG);
    }

    static iconIsSystem(icon: IHasUserState) {
        return !icon.isUserIcon;
    }
}

export class IconService {
    static $inject = ['$httpAuth', '$q'];
    static readonly serviceName = 'gpIconService';
    private allIconsDeferred: ng.IDeferred<IIconInfo[]> | null = null;
    private serviceBase = __gameplan_service_url || 'http://localhost/';

    constructor(public $http: ISimpleHttpService, public $q: ng.IQService) {
    }

    private static convertIcons(src: IIconFromServer[]): IIconInfo[] {
        // Filter out any nulls generated in the conversion.
        return src.map(i => IconService.convertIcon(i))
            .filter(i => i !== null) as IIconInfo[];
    }

    private static convertIcon(src: IIconFromServer | null | undefined): IIconInfo | null {
        if (src) {
            return {
                baseX: src.baseX,
                baseY: src.baseY,
                category: src.category,
                id: src.id,
                isUserIcon: !src.isSystemIcon,
                name: src.name,
                tags: src.tags || [],
                url: src.url,
                isDeleted: src.isDeleted,
                shortcode: src.shortcode,
            };
        } else {
            return null;
        }
    }


    public getIcon(iconId: string): ng.IPromise<IIconInfo | null> {

        let deferred = this.$q.defer<IIconInfo | null>();

        let me = this;
        let request = iconId && iconId.length > 0
            ? this.$http.get<IIconFromServer>(`${me.serviceBase}api/gameplan/icons/${iconId}`)
            : this.$http.get<IIconFromServer>(`${me.serviceBase}api/gameplan/icons/`);

        request.then(response => deferred.resolve(IconService.convertIcon(response.data)));
        request.catch(err => deferred.reject(err));

        return deferred.promise;
    }

    public getIcons(): ng.IPromise<IIconInfo[]> {
        return this.doGetIcons({ forceLoad: false });
    }

    /**
     * The built-in system icons WITH their AI-chat shortcodes
     * (?source=system). The endpoint generates the shortcode/keyword
     * metadata on first use, so the returned icons always carry a stable
     * shortcode the AI chat can reference. Not cached with getIcons() — the
     * caller (chat tab) caches per session.
     */
    public getSystemIconsForAi(): ng.IPromise<IIconInfo[]> {
        let me = this;
        let deferred = this.$q.defer<IIconInfo[]>();
        let request = this.$http.get<IIconFromServer[]>(`${me.serviceBase}api/gameplan/icons/?source=system`);
        request.then(response => deferred.resolve(IconService.convertIcons(response.data || [])));
        request.catch(err => deferred.reject(err));
        return deferred.promise;
    }

    public getIconsNoCache(options?: { includeDeleted: boolean }): ng.IPromise<IIconInfo[]> {

        return this.doGetIcons({ forceLoad: true, includeDeleted: !!options?.includeDeleted });
    }

    private doGetIcons(options?: { forceLoad?: boolean, includeDeleted?: boolean }): ng.IPromise<IIconInfo[]> {

        if (this.allIconsDeferred && !options?.forceLoad) {
            return this.allIconsDeferred.promise;
        }

        let me = this;
        let deferred = this.$q.defer<IIconInfo[]>();

        this.allIconsDeferred = deferred;
        let request = this.$http.get<IIconFromServer[]>(`${me.serviceBase}api/gameplan/icons/?includeDeleted=${!!options?.includeDeleted}`);

        request.then(response => deferred.resolve(IconService.convertIcons(response.data || [])));
        request.catch(err => deferred.reject(err));

        return deferred.promise;
    }

    public getIconsForCategory(category: string): ng.IPromise<IIconInfo[]> {

        let me = this;

        if (category && category.length > 0) {
            let deferred = this.$q.defer<IIconInfo[]>();
            let request = this.$http.get<IIconFromServer[]>(`${me.serviceBase}api/gameplan/icons/categories/${category}`);
            request.then(response => deferred.resolve(IconService.convertIcons(response.data || [])));
            request.catch(err => deferred.reject(err));

            return deferred.promise;
        } else {
            return this.getIcons();
        }

    }

    public deleteIcon(id: string): ng.IPromise<any> {
        let me = this;
        let deferred = this.$q.defer<any>();

        let request = this.$http.delete(`${me.serviceBase}api/gameplan/icons/${id}`);

        request.then(result => deferred.resolve(result));
        request.catch(err => deferred.reject(err));

        return deferred.promise;
    }


    public undeleteIcon(id: string): ng.IPromise<any> {
        let me = this;
        let deferred = this.$q.defer<any>();

        let request = this.$http.delete(`${me.serviceBase}api/gameplan/icons/${id}?undelete=true`);

        request.then(result => deferred.resolve(result));
        request.catch(err => deferred.reject(err));

        return deferred.promise;
    }

    public editIcon(id: string, newValuesSrc: INewIconValues): ng.IPromise<any> {
        let me = this;
        let deferred = this.$q.defer<any>();

        let newValues: IEditIconBindingModel = {
            name: newValuesSrc.name,
            category: newValuesSrc.category,
            tags: newValuesSrc.tags,
            url: newValuesSrc.url,
            baseX: newValuesSrc.baseX,
            baseY: newValuesSrc.baseY,
        }

        let request = this.$http.post(`${me.serviceBase}api/gameplan/icons/${id}`, newValues);

        request.then(result => deferred.resolve(result));
        request.catch(err => deferred.reject(err));

        return deferred.promise;
    }

    public uploadPersonalIcons(files: FileList) {
        return this.uploadIcons(`${this.serviceBase}api/gameplan/icons/currentuser`, files);
    }

    public uploadCompanyLogo(files: FileList) {
        return this.uploadIcons(`${this.serviceBase}api/gameplan/icons/companylogo`, files);
    }

    public uploadPublicIcons(files: FileList) {
        return this.uploadIcons(`${this.serviceBase}api/gameplan/icons/`, files);
    }

    private checkSizes(files: FileList): boolean {
        let totalSize = 0;
        for (let i = 0; i < files.length; ++i) {
            let file = files[i];
            totalSize += file.size;
        }

        return totalSize < ICON_UPLOAD_MAX_SIZE_BYTES;
    }

    private uploadIcons(url: string, files: FileList): ng.IPromise<IconsReturnModel> {

        if (!this.checkSizes(files)) {
            return this.$q.reject(ICON_UPLOAD_ERROR_TOO_BIG);
        }

        let deferred = this.$q.defer<IconsReturnModel>();

        let fd = new FormData();

        for (let i = 0; i < files.length; ++i) {
            const file = files.item(i);
            if (file) {
                fd.append('file', file, file.name);
            }
        }

        let request = this.$http.post<IconsReturnModel>(url, fd,
            {
                // Was angular.identity, which is `function identity($) {return $;}`
                // — importing Angular for it would undo this module's split.
                // Means "post the FormData unchanged", as $http requires.
                transformRequest: (value: unknown) => value,
                headers: { 'Content-Type': undefined }
            });

        request
            .then(result => deferred.resolve(result.data))
            .catch(err => deferred.reject(err))
            // after uploading, we need to make sure future requests include the new icon
            .finally(() => this.allIconsDeferred = null);

        return deferred.promise;
    }
}
